import {useState} from 'react'
import {Label} from '@/components/ui/label'
import {TagInput} from '@/components/ui/tag-input'
import {useValueSourceResolver} from './useValueSourceResolver'
import {getHintText, validateValue} from '@/lib/attribute-validation'
import {useI18n} from '@/i18n'
import type {AttributeDefinition} from '@/types/attribute-definition'
import type {SetAttributeFormValue} from '@/features/onboarding/types'

interface MultiValueAttributeInputProps {
    definition: AttributeDefinition
    values: SetAttributeFormValue[]
    onChange: (values: SetAttributeFormValue[]) => void
    disabled?: boolean
    testId?: string
}

/**
 * Multi-valued attribute editor used across the onboarding wizard and any form that
 * renders `AttributeLayoutRenderer`. Mirrors the Identity/Party detail UX: badges for
 * committed values + a single input with suggestions when a value list is attached.
 *
 * Replaces the per-row Radix Select / Input + Add button stack that used to live in
 * the two renderers (issue #299 follow-up). Keep one pattern for one concept.
 */
export function MultiValueAttributeInput({
    definition,
    values,
    onChange,
    disabled,
    testId,
}: MultiValueAttributeInputProps) {
    const i18n = useI18n()
    const suggestions = useValueSourceResolver(definition)
    const [pending, setPending] = useState('')
    const [pendingError, setPendingError] = useState<string | null>(null)

    const currentStrings = values
        .filter((v) => v.attributeName === definition.name)
        .map((v) => v.valueString ?? '')
        .filter((s) => s !== '')

    const maxItems = definition.validationRules?.maxOccurrences

    function replaceDefValues(next: string[]) {
        const otherValues = values.filter((v) => v.attributeName !== definition.name)
        const nextEntries = next.map(
            (s) =>
                ({
                    attributeName: definition.name,
                    valueType: definition.valueType,
                    valueString: s,
                }) as SetAttributeFormValue
        )
        onChange([...otherValues, ...nextEntries])
    }

    function handleAdd(val: string) {
        const trimmed = val.trim()
        if (!trimmed) return
        if (currentStrings.includes(trimmed)) {
            setPending('')
            return
        }
        const err = validateValue(definition, trimmed, i18n)
        if (err) {
            setPendingError(err)
            return
        }
        replaceDefValues([...currentStrings, trimmed])
        setPending('')
        setPendingError(null)
    }

    function handleRemove(idx: number) {
        replaceDefValues(currentStrings.filter((_, i) => i !== idx))
    }

    return (
        <div className="space-y-1.5">
            <Label htmlFor={testId ?? `attr-${definition.name}-input`} className="text-sm">
                {definition.displayName || definition.name}
                {definition.required && <span className="text-destructive ml-0.5">*</span>}
            </Label>
            <TagInput
                values={currentStrings}
                onAdd={handleAdd}
                onRemove={handleRemove}
                canRemoveItem={() => !(definition.required && currentStrings.length <= 1)}
                pending={pending}
                onPendingChange={(v) => {
                    setPending(v)
                    setPendingError(null)
                }}
                placeholder={definition.displayName || definition.name}
                disabled={disabled}
                error={pendingError}
                hint={getHintText(definition, i18n)}
                maxItems={maxItems}
                testId={testId ?? `attr-${definition.name}`}
                suggestions={suggestions.length > 0 ? suggestions : undefined}
            />
        </div>
    )
}
